/**
 * directus.js — Centralised Directus API client
 *
 * All communication with the Directus backend lives here. Nothing outside
 * this file should construct fetch() calls or WebSocket connections directly.
 *
 * Backend: https://staff.innomalous.com  (configured via REACT_APP_DIRECTUS_URL)
 *
 * Auth strategy (environment-split):
 *   Production  — Session-based (httpOnly cookie). Every request sends
 *                 credentials:'include' so the browser forwards the cookie.
 *   Development — JSON token mode. Directus returns access_token +
 *                 refresh_token in the response body; these are stored in
 *                 localStorage and sent as 'Authorization: Bearer …' headers.
 *                 Cookies don't work on localhost due to cross-origin
 *                 restrictions between localhost:3000 and staff.innomalous.com.
 *
 * Token auto-refresh (dev only):
 *   On a 401, request() silently tries /auth/refresh once. If that also fails,
 *   it clears the stored tokens and throws so AuthContext can redirect to login.
 *
 * Custom endpoints (prefixed /attendance/…) are Directus Flow hooks that
 * encapsulate the attendance state machine; they are not standard CRUD.
 */

const BASE_URL = process.env.REACT_APP_DIRECTUS_URL || 'https://staff.innomalous.com';

// True in `npm start`, false in `npm run build` — CRA sets this automatically.
const IS_DEV = process.env.NODE_ENV === 'development';

// ── Dev token store ───────────────────────────────────────────────────────────
// Only used when IS_DEV is true. Tokens survive hot-reloads via localStorage.
// Keys are distinct from the prod 'currentUser' key so they never collide.

const getDevAccessToken = () => localStorage.getItem('dev_access_token');

function setDevTokens(access, refresh) {
  localStorage.setItem('dev_access_token', access);
  localStorage.setItem('dev_refresh_token', refresh);
}

function clearDevTokens() {
  localStorage.removeItem('dev_access_token');
  localStorage.removeItem('dev_refresh_token');
}

/**
 * Exchange the stored refresh token for a new access token (dev only).
 * Directus rotates the refresh token on every call, so we persist both.
 */
async function refreshDevToken() {
  const refresh_token = localStorage.getItem('dev_refresh_token');
  if (!refresh_token) throw new Error('No refresh token available');
  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token, mode: 'json' }),
  });
  const data = await res.json();
  if (!res.ok) throw data;
  setDevTokens(data.data.access_token, data.data.refresh_token);
  return data.data.access_token;
}

// ── Session refresh ───────────────────────────────────────────────────────────

/**
 * Renew the active session without requiring the user to log in again.
 *
 * Production: POST /auth/refresh with mode:'session' tells Directus to rotate
 *   the httpOnly session cookie. The new cookie is set automatically by the
 *   browser — no token is returned to JavaScript. Called every ~23 hours by
 *   AuthContext and before every WebSocket reconnect attempt.
 *
 * Development: delegates to refreshDevToken(), which exchanges the stored
 *   refresh token for a new access token via the JSON token flow.
 */
export async function refreshSession() {
  if (IS_DEV) {
    await refreshDevToken();
    return;
  }
  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode: 'session' }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw data;
  }
}

// ── Core request ──────────────────────────────────────────────────────────────

/**
 * Thin wrapper around fetch that:
 * - Prepends BASE_URL
 * - In prod: sends the session cookie via credentials:'include'
 * - In dev:  injects 'Authorization: Bearer <token>' and auto-refreshes on 401
 * - Defaults Content-Type to JSON
 * - Throws the raw Directus error payload on non-2xx so callers can read
 *   `err.errors[0].message` for user-facing messages.
 *
 * @param {string} path           - URL path (e.g. '/items/workers')
 * @param {RequestInit} [options] - fetch options (method, body, headers…)
 * @param {boolean} [_retry]      - internal flag; prevents infinite refresh loops
 * @returns {Promise<any>} Parsed JSON body
 * @throws {TypeError} on network failure (no connectivity) — propagated as-is
 *   so AuthContext can distinguish it from a Directus auth rejection.
 * @throws {{ errors: Array<{message: string}> }} on auth failure (401 that
 *   cannot be recovered by a token/session refresh).
 */
async function request(path, options = {}, _retry = true) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (IS_DEV) {
    const token = getDevAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    ...(IS_DEV ? {} : { credentials: 'include' }),
    headers,
  });

  // Dev: silently refresh an expired access token and retry the request once
  if (IS_DEV && res.status === 401 && _retry) {
    try {
      await refreshDevToken();
      return request(path, options, false);
    } catch (refreshErr) {
      clearDevTokens();
      // Propagate network failures as-is so AuthContext can distinguish them
      // from a real auth rejection (Directus error with errors[] array).
      if (refreshErr instanceof TypeError) throw refreshErr;
      throw { errors: [{ message: 'Session expired. Please log in again.' }] };
    }
  }

  // Prod: silently refresh an expired session cookie and retry the request once
  if (!IS_DEV && res.status === 401 && _retry) {
    try {
      await refreshSession();
      return request(path, options, false);
    } catch (refreshErr) {
      // Propagate network failures as-is so AuthContext can distinguish them
      // from a real auth rejection (Directus error with errors[] array).
      if (refreshErr instanceof TypeError) throw refreshErr;
      throw { errors: [{ message: 'Session expired. Please log in again.' }] };
    }
  }

  const data = await res.json();
  if (!res.ok) throw data;
  return data;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

/**
 * Authenticate with Directus.
 *
 * Production: session mode — Directus sets an httpOnly cookie, no token in JS.
 * Development: JSON mode — tokens are returned in the response and stored in
 *              localStorage so subsequent requests can attach them as headers.
 *
 * @param {string} email
 * @param {string} password
 */
export async function login(email, password) {
  if (IS_DEV) {
    const data = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, mode: 'json' }),
    });
    setDevTokens(data.data.access_token, data.data.refresh_token);
    return data;
  }
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, mode: 'session' }),
  });
}

/**
 * Invalidate the current session on the server side.
 * AuthContext also clears the 'currentUser' localStorage entry after this resolves.
 * In dev mode we also pass the refresh token so Directus can invalidate it server-side.
 */
export async function logout() {
  if (IS_DEV) {
    const refresh_token = localStorage.getItem('dev_refresh_token');
    clearDevTokens();
    if (refresh_token) {
      // Fire-and-forget; swallow errors so the UI clears regardless
      return request('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refresh_token }),
      }).catch(() => {});
    }
    return;
  }
  return request('/auth/logout', { method: 'POST' });
}

/**
 * Fetch the authenticated user's profile including nested role.level.
 * `fields=*.*` expands one level of relations (role, avatar file object).
 *
 * @returns {Promise<{data: DirectusUser}>}
 */
export async function getCurrentUser() {
  return request('/users/me?fields=*.*');
}

// ── Workers ───────────────────────────────────────────────────────────────────

/**
 * All active workers sorted by name.
 * Used for roster display — inactive/terminated workers are excluded.
 *
 * @returns {Promise<{data: Worker[]}>}
 */
export async function getAllWorkers() {
  return request('/items/workers?filter[status][_eq]=active&sort=name');
}

/**
 * All workers (active and inactive) with full nested relations.
 * Used by ViewAllWorkersPage for the complete roster view — unlike
 * getAllWorkers() this skips the status filter and expands shift + rate +
 * photo three levels deep so cards can display all metadata without extra requests.
 *
 * @returns {Promise<{data: Worker[]}>}
 */
export async function getAllWorkersWithDetails() {
  // history is an O2M alias — it must be requested explicitly alongside *.*.*
  return request('/items/workers?fields=*.*.*,history.*&sort=name');
}

/**
 * Full worker detail with deeply nested relations (shift, rate, photo).
 * `fields=*.*.*` expands three levels so the UI can read shift.name,
 * rate.daily_rate, photo.filename_disk without extra requests.
 *
 * @param {string|number} id - Worker item ID
 * @returns {Promise<{data: Worker}>}
 */
export async function getWorker(id) {
  return request(`/items/workers/${id}?fields=*.*.*`);
}

/**
 * Create a new worker record.
 *
 * @param {Partial<Worker>} data - Worker fields (name, phone, doj, shift, rate, photo, status…)
 * @returns {Promise<{data: Worker}>}
 */
export async function createWorker(data) {
  return request('/items/workers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Upload a file to the Directus Files collection.
 * Must use multipart/form-data — cannot use the JSON `request()` wrapper
 * because fetch sets the Content-Type boundary automatically when given FormData.
 *
 * @param {FormData} formData - Must contain a `file` field
 * @param {string|null} [folderId] - Optional Directus folder UUID to place the file in
 * @returns {Promise<{data: {id: string, filename_disk: string}}>}
 */
export async function uploadFile(formData, folderId = null) {
  if (folderId) formData.append('folder', folderId);
  // No Content-Type header — fetch auto-sets multipart/form-data with boundary
  const headers = {};
  if (IS_DEV) {
    const token = getDevAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE_URL}/files`, {
    method: 'POST',
    ...(IS_DEV ? {} : { credentials: 'include' }),
    headers,
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw data;
  return data;
}

// ── Shifts & Rates ────────────────────────────────────────────────────────────

/**
 * All published shift templates (name, in_time, out_time, break_hrs).
 * Used to populate dropdowns in the wages form and new-worker form.
 *
 * @returns {Promise<{data: Shift[]}>}
 */
export async function getShifts() {
  return request('/items/shifts?filter[status][_eq]=published');
}

/**
 * All published rate cards (name, daily_rate).
 * Combined with a shift, rate determines the worker's hourly wage.
 *
 * @returns {Promise<{data: Rate[]}>}
 */
export async function getRates() {
  return request('/items/rates?filter[status][_eq]=published');
}

/**
 * All published leave policies (name, id).
 * Used to populate the leave policy dropdown in the new-worker form.
 *
 * @returns {Promise<{data: {id: number, name: string}[]}>}
 */
export async function getLeavePolicies() {
  return request('/items/leave_policies?filter[status][_eq]=published&fields=id,name&sort=name');
}

/**
 * All published teams (name, id).
 * Used to populate the team dropdown in the new-worker form.
 *
 * @returns {Promise<{data: {id: number, name: string}[]}>}
 */
export async function getTeams() {
  return request('/items/teams?filter[status][_eq]=published&fields=id,name&sort=name');
}

/**
 * All approved power days (status = 'approved').
 * Used to show the ⚡ badge on the homepage and in DayDrawer, and to add a
 * flat power day bonus during the attendance approval flow when the worker's
 * rate plan has has_powerDay = true.
 *
 * @returns {Promise<{data: {id: number, date: string, description: string|null}[]}>}
 */
export async function getPowerDays() {
  return request('/items/power_days?filter[status][_eq]=approved&fields=id,date,description');
}

// ── Attendance ────────────────────────────────────────────────────────────────

/**
 * All attendance records for a given date, sorted by worker name.
 * This is the daily roster — one record per registered worker.
 *
 * @param {Date|string} date - Any value accepted by `new Date()`
 * @returns {Promise<{data: AttendanceRecord[]}>}
 */
export async function getAttendance(date) {
  const dateStr = new Date(date).toISOString().slice(0, 10);
  return request(
    `/items/attendance?fields=*.*.*.*&filter[date][_eq]=${dateStr}&sort=worker.name`
  );
}

/**
 * Full attendance history for a single worker, newest first.
 * Used in ViewWorkerPage to build the attendance tab.
 *
 * @param {string|number} workerId
 * @returns {Promise<{data: AttendanceRecord[]}>}
 */
export async function getWorkerAttendance(workerId) {
  return request(
    `/items/attendance?fields=*.*.*` +
    `,breaks.break_in.user_created.first_name,breaks.break_in.user_updated.first_name` +
    `,breaks.break_out.user_created.first_name,breaks.break_out.user_updated.first_name` +
    `&filter[worker][_eq]=${workerId}&sort=-date&limit=-1`
  );
}

// ── Attendance state-machine endpoints ────────────────────────────────────────
// These are custom Directus Flow hooks, not standard CRUD operations.
// Each transition validates the current status server-side before committing.

/** Mark worker as present and record punch-in timestamp. */
export async function startShift(attendance_id) {
  return request('/attendance/start-shift', {
    method: 'POST',
    body: JSON.stringify({ attendance_id }),
  });
}

/** End the active shift and record punch-out timestamp (→ checkout). */
export async function endShift(attendance_id) {
  return request('/attendance/end-shift', {
    method: 'POST',
    body: JSON.stringify({ attendance_id }),
  });
}

/** Record break start (→ break). */
export async function startBreak(attendance_id) {
  return request('/attendance/start-break', {
    method: 'POST',
    body: JSON.stringify({ attendance_id }),
  });
}

/** End the active break (→ present). */
export async function endBreak(attendance_id) {
  return request('/attendance/end-break', {
    method: 'POST',
    body: JSON.stringify({ attendance_id }),
  });
}

/** Mark worker as absent for the day. */
export async function markAbsent(attendance_id) {
  return request('/attendance/mark-absent', {
    method: 'POST',
    body: JSON.stringify({ attendance_id }),
  });
}

/**
 * Mark absent and record the reason in a single request.
 * The /mark-absent endpoint now accepts an optional absent_reason field and
 * writes it to the attendance record in the same update.
 *
 * @param {number} attendance_id
 * @param {string|null} absent_reason - one of: personal | medical | family | noshow | other
 */
export async function markAbsentWithReason(attendance_id, absent_reason) {
  return request('/attendance/mark-absent', {
    method: 'POST',
    body: JSON.stringify({ attendance_id, ...(absent_reason && { absent_reason }) }),
  });
}

/**
 * Manager approval: sets is_approved = true on any terminal status
 * (checkout, absent, leave, weekly_off). For checkout records, the
 * caller must create an earnings record first (enforced server-side).
 * Status is preserved — only the is_approved flag changes.
 */
export async function approveAttendance(attendance_id) {
  return request('/attendance/approve', {
    method: 'POST',
    body: JSON.stringify({ attendance_id }),
  });
}

/**
 * Mark worker as on leave for the day (unmarked/absent → leave).
 * leave_type_id is required — the server links any matching approved
 * leave_request automatically.
 *
 * @param {number} attendance_id
 * @param {number} leave_type_id - ID of the leave_types record to apply
 */
export async function markLeave(attendance_id, leave_type_id) {
  return request('/attendance/mark-leave', {
    method: 'POST',
    body: JSON.stringify({ attendance_id, leave_type_id }),
  });
}

/** Mark worker as weekly off for the day (unmarked → weekly_off). */
export async function markWeeklyOff(attendance_id) {
  return request('/attendance/mark-weekly-off', {
    method: 'POST',
    body: JSON.stringify({ attendance_id }),
  });
}

/**
 * PATCH arbitrary fields on an attendance record.
 * Used by DayDrawer to update leave_type in real-time when a manager changes
 * the leave type selector before approving.
 *
 * @param {number} attendance_id
 * @param {object} fields - Partial attendance fields to update
 */
export async function patchAttendance(attendance_id, fields) {
  return request(`/items/attendance/${attendance_id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
}

/**
 * Lock a non-shift attendance record by setting is_approved = true directly.
 * Used for absent, leave, and weekly_off records.
 *
 * The /attendance/approve extension endpoint only accepts checkout status, so
 * non-shift approvals bypass it and PATCH the flag directly. Checkout records
 * continue to use approveAttendance() which also validates an earnings record.
 *
 * @param {number} attendance_id
 */
export async function setAttendanceApproved(attendance_id) {
  return request(`/items/attendance/${attendance_id}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_approved: true }),
  });
}

/**
 * Admin-only: reset any status back to unmarked.
 * Used to correct mistakes; does not delete earnings or payouts.
 */
export async function resetAttendance(attendance_id) {
  return request('/attendance/reset-attendance', {
    method: 'POST',
    body: JSON.stringify({ attendance_id }),
  });
}

/**
 * Manually set punch-in and punch-out times.
 * Used when a worker forgot to clock in/out. Timestamps must include
 * the IST offset (+05:30) so the server stores the correct UTC value.
 *
 * @param {string|number} attendance_id
 * @param {string} time_in  - ISO 8601 with timezone, e.g. "2024-01-15T09:00:00+05:30"
 * @param {string} time_out - ISO 8601 with timezone
 */
export async function manualAttendance(attendance_id, time_in, time_out) {
  return request('/attendance/manual-attendance', {
    method: 'POST',
    body: JSON.stringify({ attendance_id, time_in, time_out }),
  });
}

// ── Earnings ──────────────────────────────────────────────────────────────────

/**
 * Full earnings history for a worker, newest first.
 * Each record represents one day's wage entry (type: 'wages' or bonus types).
 *
 * @param {string|number} workerId
 * @returns {Promise<{data: EarningsRecord[]}>}
 */
export async function getWorkerEarnings(workerId) {
  return request(
    `/items/earnings?fields=*.*.*&filter[worker][_eq]=${workerId}&sort=-date&limit=-1`
  );
}

/**
 * Earnings records for a specific worker on a specific date.
 * Used by AttendanceDetailModal to check whether wages were already entered.
 *
 * @param {string|number} workerId
 * @param {string} date - ISO date string "YYYY-MM-DD"
 */
export async function getDaysEarningsForWorker(workerId, date) {
  return request(
    `/items/earnings?filter[worker][_eq]=${workerId}&filter[date][_eq]=${date}`
  );
}

/**
 * Create a new earnings record.
 * Always sets status to 'approved' — earnings are approved at point of entry.
 *
 * @param {Partial<EarningsRecord>} data
 */
export async function addEarnings(data) {
  return request('/items/earnings', {
    method: 'POST',
    body: JSON.stringify({ ...data, status: 'approved' }),
  });
}

// ── Payouts ───────────────────────────────────────────────────────────────────

/**
 * Full payout history for a worker, newest first.
 * Payouts reduce the worker's current_balance (computed by Directus triggers).
 *
 * @param {string|number} workerId
 */
export async function getWorkerPayouts(workerId) {
  return request(
    `/items/payouts?fields=*.*.*&filter[worker][_eq]=${workerId}&sort=-date&limit=-1`
  );
}

/**
 * Payouts for a specific worker on a specific date.
 * Used by AttendanceDetailModal to show same-day advance payments.
 *
 * @param {string|number} workerId
 * @param {string} date - ISO date "YYYY-MM-DD"
 */
export async function getDaysPayoutsForWorker(workerId, date) {
  return request(
    `/items/payouts?filter[worker][_eq]=${workerId}&filter[date][_eq]=${date}`
  );
}

/**
 * Record a payout (advance / salary disbursement).
 * Mode is one of: 'upi' | 'bank' | 'cash'.
 *
 * @param {{worker: string, date: string, mode: string, amount: number, remarks?: string}} data
 */
export async function addPayout(data) {
  return request('/items/payouts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Notes ─────────────────────────────────────────────────────────────────────

/**
 * All notes for a worker, sorted by the attendance date of the linked shift
 * (newest shift first). Sorted on attendance.date rather than date_created so
 * notes added retroactively to past records appear in the correct chronological
 * position.
 *
 * @param {string|number} workerId
 */
export async function getWorkerNotes(workerId) {
  return request(
    `/items/notes?fields=*.*.*&filter[worker][_eq]=${workerId}&sort=-attendance.date&limit=-1`
  );
}

/**
 * All notes linked to a specific attendance record.
 * Filters by the attendance FK directly so notes on past records are found
 * regardless of when they were written (date_created is the write time, not
 * the shift date — filtering by it broke retroactive note-adding).
 *
 * @param {string|number} attendanceId
 */
export async function getAttendanceNotes(attendanceId) {
  return request(
    `/items/notes?fields=*.*&filter[attendance][_eq]=${attendanceId}&sort=date_created`
  );
}

/**
 * Create a new supervisor note. Attendance is required — notes must always be
 * anchored to a specific shift record so they can be retrieved reliably.
 *
 * @param {string|number} worker     - Worker ID
 * @param {string|number} attendance - Attendance record ID
 * @param {string} content           - Note text
 */
export async function addNote(worker, attendance, content) {
  return request('/items/notes', {
    method: 'POST',
    body: JSON.stringify({ worker, attendance, content }),
  });
}

/**
 * Edit the content of an existing note. Manager only — enforced by the caller UI.
 *
 * @param {string|number} id      - Note ID
 * @param {string}        content - Updated note text
 */
export async function updateNote(id, content) {
  return request(`/items/notes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ content }),
  });
}

// ── Punches ───────────────────────────────────────────────────────────────────

/**
 * Update a single punch timestamp (punch-in, punch-out, break-in, break-out).
 * Managers only — enforced by the caller UI. PATCHes the punches collection directly.
 *
 * @param {number} punchId - ID of the punch record
 * @param {string} timestamp - ISO 8601 with timezone e.g. "2026-05-11T09:00:00+05:30"
 */
export async function updatePunchTimestamp(punchId, timestamp) {
  return request(`/items/punches/${punchId}`, {
    method: 'PATCH',
    body: JSON.stringify({ timestamp }),
  });
}


// ── Leave ─────────────────────────────────────────────────────────────────────

/**
 * Fetch a single published leave_type by its unique code slug.
 * Returns the first match or null. Used to load deduction rules for
 * status-based approvals (e.g. code='absent') without requiring a leave_request.
 *
 * @param {string} code - e.g. 'absent', 'uninformed', 'approved'
 * @returns {Promise<LeaveType|null>}
 */
export async function getLeaveTypeByCode(code) {
  const res = await request(
    `/items/leave_types?filter[code][_eq]=${encodeURIComponent(code)}` +
    `&filter[status][_eq]=published` +
    `&fields=id,name,code,paid_leave,penalty_factor,description&limit=1`
  );
  return res.data?.[0] ?? null;
}

/**
 * Fetch a leave policy with all its linked leave types, expanded through the
 * M2M junction (leave_policies_leave_types → leave_types_id).
 *
 * @param {number} policy_id - workers.leave_policy value
 * @returns {Promise<{data: {id, name, leave_types: Array<{leave_types_id: LeaveType}>}}>}
 */
export async function getLeavePolicy(policy_id) {
  return request(
    `/items/leave_policies/${policy_id}?` +
    `fields=id,name,description,` +
    `leave_types.leave_types_id.id,leave_types.leave_types_id.name,` +
    `leave_types.leave_types_id.code,leave_types.leave_types_id.approved_leave,` +
    `leave_types.leave_types_id.paid_leave,leave_types.leave_types_id.penalty_factor,` +
    `leave_types.leave_types_id.description,leave_types.leave_types_id.status`
  );
}

/**
 * Return all approved leave_requests for a worker that cover a given calendar
 * date. Makes two parallel requests (single-day and multi-day) and merges them.
 *
 * @param {number|string} worker_id
 * @param {string} date - "YYYY-MM-DD" IST date
 * @returns {Promise<{data: LeaveRequest[]}>}
 */
export async function getApprovedLeaveRequestsForDate(worker_id, date) {
  const fields =
    'id,leave_type.id,leave_type.name,leave_type.code,' +
    'leave_type.approved_leave,leave_type.paid_leave,leave_type.penalty_factor,' +
    'leave_duration,date,start_date,end_date,reason,status';

  const [single, multi] = await Promise.all([
    request(
      `/items/leave_requests?fields=${fields}` +
      `&filter[worker][_eq]=${worker_id}&filter[status][_eq]=approved` +
      `&filter[leave_duration][_eq]=single&filter[date][_eq]=${date}`
    ),
    request(
      `/items/leave_requests?fields=${fields}` +
      `&filter[worker][_eq]=${worker_id}&filter[status][_eq]=approved` +
      `&filter[leave_duration][_eq]=multiple` +
      `&filter[start_date][_lte]=${date}&filter[end_date][_gte]=${date}`
    ),
  ]);
  return { data: [...(single.data ?? []), ...(multi.data ?? [])] };
}

/**
 * All leave requests for a worker, newest first.
 * Includes expanded leave_type and user_approved for display in ViewWorkerPage.
 *
 * @param {number|string} worker_id
 */
export async function getWorkerLeaveRequests(worker_id) {
  return request(
    `/items/leave_requests?` +
    `fields=*,leave_type.id,leave_type.name,leave_type.code,` +
    `leave_type.approved_leave,leave_type.paid_leave,leave_type.penalty_factor,` +
    `user_approved.id,user_approved.first_name,user_approved.last_name` +
    `&filter[worker][_eq]=${worker_id}&sort=-date_created&limit=-1`
  );
}

/**
 * Create a new leave request.
 * Pass status: 'draft' for save-as-draft, or status: 'submitted' to submit immediately.
 *
 * @param {{
 *   worker_id: number,
 *   leave_type_id: number,
 *   leave_duration: 'single'|'multiple',
 *   date?: string,
 *   start_date?: string,
 *   end_date?: string,
 *   reason?: string,
 *   status: 'draft'|'submitted',
 * }} params
 */
export async function createLeaveRequest({ worker_id, leave_type_id, leave_duration, date, start_date, end_date, reason, status }) {
  const body = {
    worker: worker_id,
    leave_type: leave_type_id,
    leave_duration,
    status,
  };
  if (leave_duration === 'single') {
    body.date = date;
  } else {
    body.start_date = start_date;
    body.end_date   = end_date;
  }
  if (reason) body.reason = reason;
  return request('/items/leave_requests', { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Update a leave request — used for status transitions and manager
 * approve/reject (which also sets user_approved + date_approved).
 *
 * @param {number} id - leave_request ID
 * @param {object} fields - e.g. { status, user_approved, date_approved }
 */
export async function updateLeaveRequest(id, fields) {
  return request(`/items/leave_requests/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
}

/**
 * Create a deduction record. Positive amount — Directus balance formula
 * subtracts total_deductions from total_earnings - total_payouts.
 *
 * @param {{
 *   worker_id: number,
 *   attendance_id?: number,
 *   date: string,
 *   amount: number,
 *   type: 'leave'|'tax'|'damages'|'adhoc',
 *   rate_id?: number,
 *   remarks?: string,
 * }} params
 */
export async function createDeduction({ worker_id, attendance_id, date, amount, type, rate_id, remarks }) {
  return request('/items/deductions', {
    method: 'POST',
    body: JSON.stringify({
      worker: worker_id,
      date,
      amount,
      type,
      ...(attendance_id ? { attendance: attendance_id } : {}),
      ...(rate_id       ? { rate: rate_id }             : {}),
      ...(remarks       ? { remarks }                   : {}),
      status: 'approved',
    }),
  });
}

/** Convenience wrapper — leave-type deduction linked to an attendance record. */
export async function createLeaveDeduction({ worker_id, attendance_id, date, amount, rate_id, remarks }) {
  return createDeduction({ worker_id, attendance_id, date, amount, type: 'leave', rate_id, remarks });
}

/**
 * Deductions history for a worker, newest first.
 * Used on ViewWorkerPage to display the deductions ledger.
 *
 * @param {number|string} worker_id
 */
export async function getWorkerDeductions(worker_id) {
  return request(
    `/items/deductions?fields=*.*&filter[worker][_eq]=${worker_id}&sort=-date&limit=-1`
  );
}

/**
 * Deduction records for a specific worker on a specific date.
 * Used by DayDrawer to show same-day deductions alongside earnings.
 *
 * @param {number|string} workerId
 * @param {string} date - ISO date "YYYY-MM-DD"
 */
export async function getDaysDeductionsForWorker(workerId, date) {
  return request(
    `/items/deductions?filter[worker][_eq]=${workerId}&filter[date][_eq]=${date}&fields=*`
  );
}

// ── WebSocket ─────────────────────────────────────────────────────────────────

/**
 * Open a Directus Realtime WebSocket subscription for today's attendance.
 *
 * Protocol (production — session cookie):
 *  1. On open: send a `subscribe` message filtered to `date === today`.
 *  2. Directus responds with `{ type: 'subscription', event: 'init', data: [...] }` — full snapshot.
 *  3. Subsequent events: `create` (new row) or `update` (changed row).
 *  4. Directus periodically sends `{ type: 'ping' }` — must reply with `{ type: 'pong' }` or
 *     the server closes the connection after ~30 s.
 *
 * Protocol (development — token mode):
 *  Steps 2-4 are the same, but before subscribing the client must first send
 *  `{ type: 'auth', access_token }` and wait for `{ type: 'auth', status: 'ok' }`.
 *  The `authPending` flag tracks this handshake so the subscribe is deferred.
 *
 * @param {Date} date         - Subscription date (today in IST)
 * @param {{
 *   onConnect?: () => void,
 *   onClose?:   () => void,
 *   onError?:   () => void,
 *   onMessage?: (data: any) => void,
 * }} handlers
 * @returns {WebSocket} The raw socket — caller must call `.close()` on unmount.
 */
export function createAttendanceWS(date, handlers) {
  const wsUrl = `wss://staff.innomalous.com/websocket`;
  const socket = new WebSocket(wsUrl);
  const today = new Date(date).toISOString().slice(0, 10);

  // In dev, we must complete the auth handshake before sending subscribe.
  // authPending starts true only in dev — in prod it's always false.
  let authPending = IS_DEV;

  const subscribeMsg = JSON.stringify({
    type: 'subscribe',
    collection: 'attendance',
    query: {
      fields: ['*.*.*.*'],
      sort: ['worker.name'],
      filter: { date: { _eq: today } },
    },
  });

  socket.onopen = () => {
    handlers.onConnect?.();
    if (IS_DEV) {
      // Authenticate first; subscribe is sent after we receive auth confirmation
      socket.send(JSON.stringify({ type: 'auth', access_token: getDevAccessToken() || '' }));
    } else {
      socket.send(subscribeMsg);
    }
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    // Must reply to ping or Directus drops the connection
    if (data.type === 'ping') {
      socket.send(JSON.stringify({ type: 'pong' }));
      return;
    }
    // Dev: complete the auth handshake, then send the deferred subscribe
    if (authPending && data.type === 'auth') {
      authPending = false;
      if (data.status === 'ok') {
        socket.send(subscribeMsg);
      } else {
        handlers.onError?.();
        socket.close();
      }
      return;
    }
    if (data.type === 'subscription') {
      handlers.onMessage?.(data);
    }
  };

  socket.onerror = () => handlers.onError?.();
  socket.onclose = () => handlers.onClose?.();

  return socket;
}

// ── Push notifications ────────────────────────────────────────────────────────

/**
 * Persists a browser PushSubscription to the Directus push_subscriptions
 * collection so the notification Worker can look it up when sending alerts.
 *
 * The record is linked to the currently authenticated Directus user via the
 * session cookie (prod) or Bearer token (dev). Directus assigns user_id
 * automatically using the $CURRENT_USER field default.
 *
 * @param {PushSubscription} subscription — from pushManager.subscribe()
 * @returns {Promise<object>} Created Directus item
 */
export async function savePushSubscription(subscription) {
  const keys = subscription.toJSON().keys ?? {};
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

  // Deduplicate — if this endpoint is already stored, return the existing row.
  // This prevents multiple rows when the same browser subscribes more than once.
  const existing = await request(
    `/items/push_subscriptions?filter[endpoint][_eq]=${encodeURIComponent(subscription.endpoint)}&fields=id&limit=1`
  );
  if (existing?.data?.length) return existing;

  return request('/items/push_subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      user_id: currentUser.id ?? null,
      endpoint: subscription.endpoint,
      p256dh: keys.p256dh ?? '',
      auth_key: keys.auth ?? '',
      user_agent: navigator.userAgent.slice(0, 250),
      enabled: true,
    }),
  });
}

/**
 * Removes a push subscription from Directus by its endpoint URL.
 * Called when the user disables notifications or the subscription is revoked.
 *
 * @param {string} endpoint — PushSubscription.endpoint value
 */
export async function deletePushSubscription(endpoint) {
  // Find matching records (should be at most one per endpoint)
  const { data } = await request(
    `/items/push_subscriptions?filter[endpoint][_eq]=${encodeURIComponent(endpoint)}&fields=id`
  );
  if (!data?.length) return;
  // Delete all matches (handles edge-case duplicates)
  await Promise.all(data.map((row) => request(`/items/push_subscriptions/${row.id}`, { method: 'DELETE' })));
}

// ── Payslips ──────────────────────────────────────────────────────────────────

/**
 * All payslips for a worker, most recent cycle first.
 * Returns only the list-view fields to keep the payload small.
 *
 * @param {string|number} workerId
 */
export async function getWorkerPayslips(workerId) {
  return request(
    `/items/payslips?filter[worker][_eq]=${workerId}&sort=-cycle_label` +
    `&fields=id,cycle_label,cycle_start,cycle_end,payout_date,status,` +
    `net_due,opening_balance,carryforward_amount,total_earnings,total_deductions,total_payouts`
  );
}

/**
 * Full payslip detail with all nested live data.
 * Used for draft payslips and as the base fetch for approved/paid
 * (snapshot fields are embedded in the top-level response).
 *
 * @param {string} id — payslip UUID
 */
export async function getPayslipDetail(id) {
  return request(
    `/items/payslips/${id}?fields=*,worker.id,worker.name,worker.doj,` +
    `earnings.*,deductions.*,payouts.*`
  );
}

/**
 * Attendance records linked to a payslip, with full punch expansion.
 * Uses the /items/attendance endpoint with fields=*.*.* — the same proven
 * expansion used by getWorkerAttendance — rather than traversing from the
 * payslips endpoint where punch_in/out nested relations don't expand.
 *
 * @param {string} payslipId — payslip UUID
 */
export async function getPayslipAttendance(payslipId) {
  return request(
    `/items/attendance?filter[payslip][_eq]=${payslipId}&fields=*.*.*&sort=date&limit=-1`
  );
}

/**
 * Generate a draft payslip for a worker for the cycle containing the given date.
 *
 * @param {string|number} workerId
 * @param {string} date — ISO date "YYYY-MM-DD" (any date within the target cycle)
 */
export async function generatePayslip(workerId, date) {
  return request('/payroll/generate', {
    method: 'POST',
    body: JSON.stringify({ worker_id: workerId, date }),
  });
}

/**
 * Recompute totals on a draft payslip from live attendance/earnings/deductions.
 * No-op on approved or paid payslips (server rejects).
 *
 * @param {string} id — payslip UUID
 */
export async function recalculatePayslip(id) {
  return request(`/payroll/${id}/recalculate`, { method: 'POST' });
}

/**
 * Freeze snapshot fields and move payslip from draft → approved.
 * Server will reject if required data is missing or status is wrong.
 *
 * @param {string} id — payslip UUID
 */
export async function approvePayslip(id) {
  return request(`/payroll/${id}/approve`, { method: 'POST' });
}

/**
 * Record payment(s) against an approved payslip and move it to paid.
 * Can link existing unlinked payout records and/or create new ones in one call.
 *
 * @param {string} id — payslip UUID
 * @param {{ existing_payout_ids?: string[], new_payouts?: Array<{date,mode,amount}> }} payload
 */
export async function markPayslipPaid(id, payload) {
  return request(`/payroll/${id}/mark-paid`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Payout records for a worker that are not yet linked to any payslip.
 * Shown in MarkPaidModal so the manager can attribute existing advances.
 *
 * @param {string|number} workerId
 */
export async function getUnlinkedPayouts(workerId) {
  return request(
    `/items/payouts?filter[worker][_eq]=${workerId}&filter[payslip][_null]=true&sort=-date`
  );
}

/**
 * Fetch the app-wide settings singleton.
 * Contains fields like payslip_autoPay_threshold and roundOff configuration.
 */
export async function getSettings() {
  return request('/items/settings');
}
